/**
 * /personnel — System Personnel (M46).
 *
 * Every person in the lane: cert number, level, state certification expiry,
 * the agencies they serve, who recertifies them, their A-B Tech ID if known.
 * Sort by any column, filter by agency, level and cert standing, search by
 * name or cert number. "Sync from intake" files new System intake
 * submissions in. People added from their skill evaluations (M50) are
 * marked "awaiting intake" until their intake matches them. Dates of birth are never shown; only how many are still
 * held for matching, so it is visible when they are gone.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../lib/api.js';
import { withLane } from '../lib/lane.js';
import ErrorBanner from '../components/ErrorBanner.jsx';
import LanePicker from '../components/LanePicker.jsx';

const DAY = new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeZone: 'UTC' });
const day = (d) => (d ? DAY.format(new Date(`${d}T00:00:00Z`)) : null);
const STANDING = {
  current: ['Current', 'badge badge--published'],
  due_soon: ['Due within 90 days', 'badge badge--warn'],
  expired: ['Expired', 'badge badge--cancelled'],
  unknown: ['No date on file', 'badge'],
};
const COLUMNS = [
  ['last_name', 'Name'],
  ['cert_number', 'Cert #'],
  ['cert_level', 'Level'],
  ['cert_expires', 'Cert expires'],
  ['primary_agency', 'Agency'],
  ['recerts_through', 'Recerts through'],
  ['abtech_id', 'A-B Tech ID'],
];

export function PersonnelPage() {
  const [data, setData] = useState(undefined);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  const [agency, setAgency] = useState('');
  const [level, setLevel] = useState('');
  const [standing, setStanding] = useState('');
  const [origin, setOrigin] = useState('');
  const [sort, setSort] = useState({ key: 'last_name', dir: 1 });
  const [syncing, setSyncing] = useState(false);
  const [synced, setSynced] = useState(null);

  const load = useCallback(async () => {
    try {
      setData(await apiFetch(withLane('/api/v1/personnel')));
      setError(null);
    } catch (err) {
      setError(err);
      setData(null);
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  async function sync() {
    setSyncing(true);
    setError(null);
    try {
      setSynced(await apiFetch('/api/v1/personnel/sync', { method: 'POST', body: JSON.stringify({ tenant: data.tenant.id }) }));
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setSyncing(false);
    }
  }

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const list = (data?.people ?? []).filter(
      (p) =>
        (!needle || `${p.full_name} ${p.cert_number ?? ''} ${p.abtech_id ?? ''}`.toLowerCase().includes(needle)) &&
        (!agency || p.agencies.some((a) => a.agency === agency)) &&
        (!level || p.cert_level === level) &&
        (!standing || p.cert_standing === standing) &&
        (!origin || (origin === 'awaiting' ? p.awaiting_intake : !p.awaiting_intake))
    );
    return [...list].sort((a, b) => {
      const x = a[sort.key] ?? '';
      const y = b[sort.key] ?? '';
      return (String(x).localeCompare(String(y)) || a.last_name.localeCompare(b.last_name)) * sort.dir;
    });
  }, [data, q, agency, level, standing, origin, sort]);

  const counts = useMemo(() => {
    const c = { current: 0, due_soon: 0, expired: 0, unknown: 0 };
    c.awaiting = 0;
    for (const p of data?.people ?? []) {
      c[p.cert_standing] += 1;
      if (p.awaiting_intake) c.awaiting += 1;
    }
    return c;
  }, [data]);

  function csv() {
    const head = ['Last', 'First', 'Cert #', 'Level', 'Cert expires', 'Primary agency', 'Other System agencies', 'Recerts through', 'A-B Tech ID', 'Email', 'Outside affiliations'];
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines = [head, ...rows.map((p) => [
      p.last_name, p.first_name, p.cert_number, p.cert_level, p.cert_expires, p.primary_agency,
      p.agencies.filter((a) => a.agency !== p.primary_agency).map((a) => a.agency).join('; '),
      p.recerts_through, p.abtech_id, p.email, p.other_affiliations.join('; '),
    ])].map((r) => r.map(esc).join(','));
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([lines.join('\n')], { type: 'text/csv' }));
    a.download = `System_Personnel_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  }

  if (data === undefined) return <p className="muted">Loading…</p>;

  return (
    <>
      <div className="page-head">
        <h1>System Personnel</h1>
        {data && <LanePicker tenant={data.tenant} tenants={data.tenants} onChange={() => load()} />}
      </div>
      <ErrorBanner error={error} />
      {data && (
        <>
          <p className="muted">
            {data.tenant?.demo ? 'The demo group — fake people. ' : 'Real people, from the System intake. '}
            {data.people.length} people · {counts.current} current · {counts.due_soon} due within 90 days · {counts.expired} expired ·{' '}
            {counts.unknown} with no expiry date on file.
            {counts.awaiting > 0 && (
              <>
                {' '}
                <strong>{counts.awaiting}</strong> added from their A-B Tech skill evaluations or the System tracker and awaiting their System intake.
              </>
            )}
            {data.dob_held != null && (
              <>
                {' '}
                Dates of birth still held for matching skill records: <strong>{data.dob_held}</strong> (never shown; to be deleted once matching is done).
              </>
            )}
          </p>

          {data.can_sync && !data.tenant?.demo && (
            <p className="toolbar">
              <button type="button" className="btn btn--auto" onClick={sync} disabled={syncing}>
                {syncing ? 'Syncing…' : 'Sync from intake'}
              </button>
              <Link className="btn btn--quiet btn--auto" to="/people-review">
                Review matches
              </Link>
              {synced && (
                <span className="muted" role="status">
                  {synced.added} added · {synced.updated} updated
                  {synced.adopted_from_evaluations ? ` · ${synced.adopted_from_evaluations} matched to people awaiting intake` : ''} ·{' '}
                  {synced.no_agency_match} without a System agency
                  {synced.evaluation_passes?.attached ? ` · ${synced.evaluation_passes.attached} skill passes attached` : ''}
                </span>
              )}
            </p>
          )}

          <section className="panel" aria-labelledby="agencies-heading">
            <h2 id="agencies-heading">Agencies ({data.agencies.length})</h2>
            <p className="muted">
              ✓ = approved to recertify its own people. Everyone else recertifies through A-B Tech. A-B Tech&apos;s own row is its
              instructors, who answer to A-B Tech&apos;s requirements above the system minimum.
            </p>
            <div className="toolbar" style={{ flexWrap: 'wrap' }}>
              {data.agencies.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  className={`btn btn--auto ${agency === a.name ? 'btn--primary' : 'btn--quiet'}`}
                  onClick={() => setAgency(agency === a.name ? '' : a.name)}
                >
                  {a.recerts_own_people ? '✓ ' : ''}
                  {a.type === 'college' ? 'A-B Tech (instructors)' : a.name} · {a.people}
                </button>
              ))}
            </div>
          </section>

          <section className="panel" aria-labelledby="people-heading">
            <h2 id="people-heading">People ({rows.length})</h2>
            <div className="field-row">
              <label className="field">
                <span>Search name, cert # or A-B Tech ID</span>
                <input type="search" value={q} onChange={(e) => setQ(e.target.value)} />
              </label>
              <label className="field">
                <span>Level</span>
                <select value={level} onChange={(e) => setLevel(e.target.value)}>
                  <option value="">All</option>
                  <option>EMR</option>
                  <option>EMT</option>
                  <option>AEMT</option>
                  <option>Paramedic</option>
                </select>
              </label>
              <label className="field">
                <span>State certification</span>
                <select value={standing} onChange={(e) => setStanding(e.target.value)}>
                  <option value="">All</option>
                  <option value="expired">Expired</option>
                  <option value="due_soon">Due within 90 days</option>
                  <option value="current">Current</option>
                  <option value="unknown">No date on file</option>
                </select>
              </label>
              <label className="field">
                <span>Came in through</span>
                <select value={origin} onChange={(e) => setOrigin(e.target.value)}>
                  <option value="">All</option>
                  <option value="intake">System intake</option>
                  <option value="awaiting">Awaiting intake (from evaluations or the tracker)</option>
                </select>
              </label>
            </div>
            <p className="toolbar">
              {agency && (
                <button type="button" className="btn btn--quiet btn--auto" onClick={() => setAgency('')}>
                  Showing {agency} — show all agencies
                </button>
              )}
              <button type="button" className="btn btn--quiet btn--auto" onClick={csv} disabled={!rows.length}>
                Download these rows (CSV)
              </button>
            </p>
            <div className="schedule__scroll">
              <table className="schedule__table">
                <thead>
                  <tr>
                    {COLUMNS.map(([key, label]) => (
                      <th key={key} scope="col" aria-sort={sort.key === key ? (sort.dir > 0 ? 'ascending' : 'descending') : 'none'}>
                        <button
                          type="button"
                          className="btn btn--quiet btn--auto"
                          onClick={() => setSort((s) => ({ key, dir: s.key === key ? -s.dir : 1 }))}
                        >
                          {label}
                          {sort.key === key ? (sort.dir > 0 ? ' ▲' : ' ▼') : ''}
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((p) => (
                    <tr key={p.roster_id}>
                      <td>
                        <strong>
                          {p.last_name}, {p.first_name}
                        </strong>
                        {p.email && <div className="muted">{p.email}</div>}
                        {p.awaiting_intake && (
                          <div>
                            <span className="badge badge--warn">
                              {p.came_from === 'tracker' ? 'from System tracker' : p.from_tracker ? 'from skill evaluations + System tracker' : 'from skill evaluations'} — awaiting intake
                            </span>
                          </div>
                        )}
                      </td>
                      <td>
                        <code>{p.cert_number ?? '—'}</code>
                      </td>
                      <td>{p.cert_level ?? <span className="muted">—</span>}</td>
                      <td>
                        {day(p.cert_expires) ?? ''} <span className={STANDING[p.cert_standing][1]}>{STANDING[p.cert_standing][0]}</span>
                      </td>
                      <td>
                        {p.agencies.map((a) => (
                          <div key={a.agency}>
                            {a.agency === p.primary_agency ? <strong>{a.agency}</strong> : <span className="muted">{a.agency}</span>}
                            {a.recerts_own_people ? ' ✓' : ''}
                          </div>
                        ))}
                        {p.other_affiliations.length > 0 && <div className="muted">also: {p.other_affiliations.join(', ')}</div>}
                      </td>
                      <td>{p.recerts_through ?? <span className="muted">—</span>}</td>
                      <td>{p.abtech_id ? <code>{p.abtech_id}</code> : <span className="muted">not on file</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </>
  );
}

export default PersonnelPage;
